// features/findings/israel-haredi-floor.js
// ════════════════════════════════════════════════════════════════════
// LAB FINDING — The Six-Plus Floor (v1)
// Form 1 — Single-case mechanism dossier. Pre-registered 2026-05-08.
//
// The fourth popdec finding. Operationalizes the load-bearing contrast
// (Israel) named in Positive-Case Search. Documents the seven structural
// conditions that produce sustained Haredi cohort fertility ≥ 6.0,
// independent of policy spending, in an OECD context.
//
// Reading order: korea-hungary-divergence (static negative) → korea-lockin
// (dynamic negative) → positive-case-search (forward negative) → this
// (structural positive). Together they form the complete arc.
// ════════════════════════════════════════════════════════════════════

import {
  FindingBack,
  FindingHeader,
  FindingSection,
  FindingSubhead,
  FindingPill,
  FindingPillRow,
} from './components-shared';
import {
  DivergenceTimeline,
  PredictionsBlock,
  MagnitudeBars,
} from './charts';

// ─── Chart data ─────────────────────────────────────────────────────

// Cohort fertility series across sub-populations and comparison anchors.
// X-axis: birth-cohort midpoint (so 1960 = 1955-65 cohort, etc).
// Sources: CBS Israel + DellaPergola (Israel sub-pops); Wittgenstein
// Centre / Human Fertility Database (Korea Seoul, Hungary urban,
// Mormon Utah). Israel-Haredi 1990 cohort is ~90% complete; its value
// is the projected completion central estimate.
const COHORT_FERTILITY_SERIES = [
  {
    id: 'haredi',
    label: 'Israel-Haredi',
    color: 'sage',
    points: [
      { x: 1960, y: 6.4 },
      { x: 1970, y: 6.6 },
      { x: 1980, y: 6.6 },
      { x: 1990, y: 6.5 },
    ],
  },
  {
    id: 'mormon-utah',
    label: 'Mormon Utah',
    color: 'forest-moss',
    points: [
      { x: 1960, y: 3.8 },
      { x: 1970, y: 3.2 },
      { x: 1980, y: 2.9 },
      { x: 1990, y: 2.7 },
    ],
  },
  {
    id: 'israel-arab',
    label: 'Israel-Arab Muslim',
    color: 'cognac',
    points: [
      { x: 1960, y: 6.5 },
      { x: 1970, y: 5.4 },
      { x: 1980, y: 4.0 },
      { x: 1990, y: 3.0 },
    ],
  },
  {
    id: 'israel-secular',
    label: 'Israel-secular Jewish',
    color: 'brass',
    points: [
      { x: 1960, y: 2.6 },
      { x: 1970, y: 2.4 },
      { x: 1980, y: 2.2 },
      { x: 1990, y: 2.1 },
    ],
  },
  {
    id: 'hungary',
    label: 'Hungary urban',
    color: 'plum',
    points: [
      { x: 1960, y: 1.9 },
      { x: 1970, y: 1.7 },
      { x: 1980, y: 1.5 },
      { x: 1990, y: 1.5 },
    ],
  },
  {
    id: 'korea-seoul',
    label: 'Korea Seoul',
    color: 'oxblood',
    points: [
      { x: 1960, y: 2.1 },
      { x: 1970, y: 1.5 },
      { x: 1980, y: 1.0 },
      { x: 1990, y: 0.55 },
    ],
  },
];

// Structural-condition load-bearing weights (per §3 of the finding).
// Each condition's "weight" estimates how much fertility intent collapses
// when that condition alone is removed from the seven-condition stack.
// Numbers are mechanism-dossier estimates, not measurements.
const CONDITION_WEIGHTS = [
  { label: 'C1 — Religious-community density (peer-parents-visibility ~100%)', value: 1.5, tone: 'sage' },
  { label: 'C3 — Alternative-status structure (yeshiva/kollel; status decoupled from income)', value: 1.3, tone: 'sage' },
  { label: 'C5 — Identity-narrative coherence around family (~95%)', value: 1.2, tone: 'sage' },
  { label: 'C2 — Multi-generational household economics (housing absorbed by family)', value: 1.0, tone: 'brass' },
  { label: 'C4 — Kinship-network childcare (cost-compression on later parities)', value: 0.9, tone: 'brass' },
  { label: 'C6 — Marriage-formation infrastructure (median age ~21F / ~22M)', value: 0.8, tone: 'cognac' },
  { label: 'C7 — Exit cost (structural stability against drift)', value: 0.7, tone: 'cognac' },
];

// Cross-case condition counts × cohort TFR — the load-bearing pattern.
// Sources: cohort fertility from Wittgenstein Centre / HFD / national
// statistics agencies; condition counts from finding §5 mechanism analysis.
const CASE_CONDITION_COUNTS = [
  { label: 'Israel-Haredi (7/7 conditions)',          value: 6.5, tone: 'sage' },
  { label: 'Hutterite US/Canada (7/7)',                value: 6.0, tone: 'sage' },
  { label: 'Mormon Utah (5-6/7)',                      value: 2.7, tone: 'forest-moss' },
  { label: 'Roma in Hungary (3-4/7)',                  value: 3.0, tone: 'cognac' },
  { label: 'Israel-Arab Muslim (3/7, declining)',     value: 3.0, tone: 'cognac' },
  { label: 'Korean rural Christian (P5 framework projection — not yet observed)', value: 1.4, tone: 'brass' },
  { label: 'Israel-secular Jewish (2-3/7)',           value: 2.1, tone: 'brass' },
  { label: 'France (1/7)',                             value: 1.62, tone: 'plum' },
  { label: 'Hungary urban (0-1/7)',                    value: 1.50, tone: 'plum' },
  { label: 'Sweden (0/7)',                             value: 1.65, tone: 'plum' },
  { label: 'Korea Seoul (0/7)',                        value: 0.55, tone: 'oxblood' },
];

// Counterfactual per-condition removal — fertility intent floor when
// each individual condition is removed from the otherwise-full 7-stack.
// Mechanism-dossier estimates, not measurements; documents the load-bearing
// claim that C1, C3, C5 are the structural backbone.
const COUNTERFACTUAL_REMOVAL = [
  { label: 'Remove C1 (religious-community density)',  value: 3.0, tone: 'oxblood' },
  { label: 'Remove C3 (alternative status structure)', value: 3.5, tone: 'oxblood' },
  { label: 'Remove C5 (identity-narrative coherence)', value: 4.5, tone: 'cognac' },
  { label: 'Remove C2 (multi-gen household)',          value: 4.0, tone: 'cognac' },
  { label: 'Remove C4 (kinship-network childcare)',    value: 5.0, tone: 'brass' },
  { label: 'Remove C6 (marriage-formation infra)',     value: 5.5, tone: 'brass' },
  { label: 'Remove C7 (exit cost)',                    value: 6.2, tone: 'sage' },
  { label: 'Full stack present (baseline)',             value: 6.5, tone: 'sage' },
];

// ─── Predictions ────────────────────────────────────────────────────

export const HAREDI_PREDICTIONS = [
  {
    label: 'P1 — Haredi 1990-95 cohort completes ≥6.0',
    central: 6.5,
    band: [6.0, 6.8],
    falsifierBand: [5.5, 7.0],
    resolutionDate: '2030 ICBS cohort tables',
    status: 'open',
    note: 'Cohort closure ~2030 (women born 1990-95 reach age 40 with ≥95% completion). Falsified if completed cohort < 5.5. The seven-condition stack predicts maintenance of the documented 1955-1985 floor.',
  },
  {
    label: 'P2 — Aggregate Haredi TFR ≥5.5 through 2035 despite share rising 13%→22%',
    central: 5.8,
    band: [5.5, 6.2],
    falsifierBand: [5.0, 6.5],
    resolutionDate: '2035 ICBS demographic projections',
    status: 'open',
    note: 'Tests whether urbanization-driven decline operates inside the Haredi sub-population as it expands. If the structural conditions are load-bearing, growth does not weaken the conditions; if conditions are sample-selected (selection-into-community), growth dilutes the structural integrity.',
  },
  {
    label: 'P3 — Mormon Utah 1985-95 cohort completes ≥2.5',
    central: 2.7,
    band: [2.5, 2.9],
    falsifierBand: [2.2, 3.1],
    resolutionDate: '2030+ US Census + cohort tables',
    status: 'open',
    note: 'Cross-case extrapolation from 5-6/7 condition count. If Mormon Utah falls below 2.2, the cross-case framework is wounded — either the count is wrong (overestimated) or the relationship is weaker than this finding claims.',
  },
  {
    label: 'P4 — Anti-policy-replication: zero state-secular OECD cases reach cohort TFR ≥5.0 by 2040',
    central: 0,
    band: [0, 0],
    falsifierBand: [0, 1],
    resolutionDate: '2040 cohort completion (1995-2000 cohort)',
    status: 'open',
    note: 'Single-falsifier-suffices design. Tests whether the seven structural conditions can be reproduced in non-religious form via policy alone. Hungarian, Korean, Japanese, French, and Swedish policy expansions 2025-2040 are in scope. Kibbutz movement of 1950s-70s is the historical counter-case (briefly ~4/7 secular conditions, did not sustain).',
  },
  {
    label: 'P5 — Korean rural Christian sub-pop cohort TFR ≥1.3 (vs Korea overall ~0.7)',
    central: 1.4,
    band: [1.3, 1.6],
    falsifierBand: [1.0, 1.8],
    resolutionDate: '2030+ KOSIS sub-stratum tables',
    status: 'open',
    note: 'Tests whether partial condition presence (2-3/7) produces partial recovery. If Korean rural Christian sub-population produces the same ~0.7 TFR as Korean Seoul, the conditions framework fails to explain within-Korea variance — a wounding result.',
  },
];

// ─── Finding metadata ───────────────────────────────────────────────

export const FINDING_META = {
  slug: 'israel-haredi-floor',
  domain: 'popdec',
  year: 2026,
  version: 'v1',
  lifecycle: 'pre-registered',
  prevVersion: null,
  revisedBy: null,
  whatChanged: null,
  retractionNote: null,
  citationHandle: 'lab:finding/popdec/2026/israel-haredi-floor/v1',
  title: 'The Six-Plus Floor: How Israel-Haredi Sustains Cohort Fertility ≥ 6.0 in an OECD Context',
  shortTitle: 'Israel-Haredi Floor',
  pageTitle: 'The Six-Plus Floor — Lab Finding · Shift',
  description:
    'Single-case mechanism dossier on the only OECD-context cohort at national-sub-population scale with documented sustained fertility ≥ 6.0. (Hutterite + Amish are valid peer cases at smaller community scale.) Seven structural conditions identified; counterfactual per-condition removal; cross-case validation across Hutterite, Mormon Utah, Roma, Israel-secular, France, Sweden, Korea, Hungary. Five pre-registered predictions resolving 2030-2040.',
  form: 1,
  formName: 'Single-case mechanism dossier',
  reflexivity: 'HIGH',
  epistemicStatus: 'mechanism',
  confidence: 'medium',
  sampleSize: 1,
  preregisteredAt: '2026-05-08',
  visibility: 'public-peek',
};

// ─── Article ────────────────────────────────────────────────────────

export default function IsraelHarediFloorFinding() {
  return (
    <article className="finding-article">
      <FindingBack href="/findings" label="← Findings index" />

      <FindingHeader
        eyebrow="§ Lab finding · popdec/2026/israel-haredi-floor/v1"
        title={FINDING_META.title}
        sub="The fourth popdec finding. The structural-positive case completing the negative-corpus arc."
        subItalic="Korea-Hungary documented that spending fails. Korea Lock-In documented when its branches closed. Positive-Case Search committed to a public falsifier on the next decade. This finding documents the only OECD-context cohort at national-sub-population scale with sustained recovery, and what specifically makes it work — independent of policy spending, mechanically, condition-by-condition. (Hutterite + Amish replicate the pattern at smaller community scale; cross-case validated in the Refugia Template finding.)"
        meta={FINDING_META}
      />

      <section className="finding-lede">
        <p>
          Three popdec findings have established that policy alone fails to reproduce sustained cohort recovery. None of them have answered the next-most-important question: <em>what does sustained cohort recovery actually look like, structurally, in the only documented OECD-context case at national-sub-population scale?</em> The Positive-Case Search names Israel as the load-bearing contrast — predicted to remain at TFR ≥ 1.70 but not falsifying the spending thesis because the wedge is religious-community structure, not policy. This finding operationalizes that contrast. Inside Israel, the Haredi sub-population sustains cohort fertility at or above 6.0 across every observed cohort since women born 1955-65. The cohort has urbanized. It has lived through the same secular-modernization pressures as every other OECD population. It continues to produce the same fertility outcome. Hutterite and Amish communities replicate the structural pattern at much smaller community scale, validating that the framework is not Israel-Haredi-specific (cross-validated in <code>lab:finding/popdec/2026/refugia-template/v1</code>). Below: the cohort data, the seven structural conditions that operate as a stack, the counterfactual removal of each condition, and the cross-case pattern that emerges when the same lens is applied to Mormon Utah, Hutterite, Roma, and Israel-secular sub-populations.
        </p>
      </section>

      <section className="finding-tldr">
        <div className="finding-tldr-eyebrow">TL;DR</div>
        <p className="finding-tldr-verdict">
          <strong>Mechanism documented:</strong> seven mutually-reinforcing structural conditions produce sustained cohort fertility ≥ 6.0 in Israel-Haredi. The first three (religious-community density, alternative-status structure, identity-narrative coherence) are load-bearing — removing any one collapses the structure. None of these conditions exist as a coherent stack in any OECD policy-intensive recovery attempt. Subsidy is permissive, not causal: same subsidy regime produces TFR 6.5 in Haredi, 3.0 in Israel-Arab Muslim, 2.1 in Israel-secular.
        </p>
        <FindingPillRow>
          <FindingPill tone="mechanism">mechanism documented</FindingPill>
          <FindingPill tone="warn">Confidence: medium</FindingPill>
          <FindingPill tone="neutral">N=1 primary case</FindingPill>
          <FindingPill tone="neutral">Pre-registered 2026-05-08</FindingPill>
          <FindingPill tone="failure">Reflexivity: HIGH</FindingPill>
        </FindingPillRow>
        <div className="finding-tldr-corpus">
          <strong>Corpus:</strong> Israel-Haredi primary; Hutterite, Mormon Utah, Roma, Korean rural Christian, Israel-secular, Israel-Arab as cross-case anchors. Sources: CBS Israel, Sergio DellaPergola demographic series, Wittgenstein Centre, Human Fertility Database, OECD Family Database. Cross-cite: <code>lab:finding/popdec/2026/korea-hungary-divergence/v1</code>, <code>lab:finding/popdec/2026/korea-lockin/v1</code>, <code>lab:finding/popdec/2026/positive-case-search/v1</code>.
        </div>
      </section>

      {/* ── §1 The puzzle ── */}
      <FindingSection
        eyebrow="§1 The puzzle"
        title="What the negative corpus implies but does not supply."
      >
        <p className="finding-prose">
          The Continuum's working thesis — <em>policy compensates at margins, cannot create the cultural-structural conditions that lift fertility</em> — is well-defended on the negative side after three findings. The Korea-Hungary pair documented that Hungary's 6.2% GDP family policy produced tempo gains that reverted; the structural variables (narrative, infrastructure, status) were inferred to be load-bearing, but not isolated. Korea's Lock-In documented that of six Korean branch points 1997-2024, three had already closed by 2005, and the structural levers were all about infrastructure, not spending. Positive-Case Search committed to a public falsifier: zero OECD policy-driven recovery to 1.70 cohort fertility by 2035.
        </p>
        <p className="finding-prose">
          What the three findings imply but do not document is the positive case: a cohort that DID sustain recovery, observable, with mechanism specified. Israel-Haredi is the only OECD-context cohort <em>at national-sub-population scale</em> sustaining cohort fertility ≥6.0 (Hutterite and Amish replicate the pattern at smaller community scale; cross-case validated in <code>lab:finding/popdec/2026/refugia-template/v1</code>). Sustained cohort fertility ≥ 6.0 across every observed cohort since women born 1955-65, in a society that has urbanized and modernized through the same secular-pressure window as every OECD comparison case. Understanding <em>how</em> answers the "what to do instead" question that the prior findings imply but cannot supply alone.
        </p>
        <p className="finding-prose">
          This finding is not normative endorsement of the Haredi structure. The high-fertility outcome carries documented social costs (low labor-force participation, especially male; higher poverty rates; secular-education deficits). The trade-offs are value-laden and not adjudicated here. The claim is descriptive: <strong>this is what sustained sub-replacement-region recovery actually looks like, structurally, in the only OECD case that has produced it.</strong>
        </p>
      </FindingSection>

      {/* ── §2 The data ── */}
      <FindingSection
        eyebrow="§2 The data"
        title="Cohort fertility series. The Haredi line is flat where the comparisons are not."
      >
        <p className="finding-prose">
          Cohort fertility is the gold-standard fertility measure: the number of children women born in a given year actually produce by the end of their reproductive lives. It is immune to the tempo confounds (postponement and reversal) that contaminate period TFR. The series below shows cohort fertility across sub-populations, cohort midpoint 1960-1990 (so the rightmost data point is the 1985-95 cohort, which is ~90% complete and reported as projected central estimate).
        </p>

        <DivergenceTimeline
          title="Cohort fertility series 1955-1995 — Haredi flat at 6.4-6.6 across the full window; every comparison cohort has fallen substantially. Sources: CBS Israel + DellaPergola for Israel sub-populations; Wittgenstein Centre + Human Fertility Database for comparisons."
          series={COHORT_FERTILITY_SERIES}
          xLabel="Birth-cohort midpoint"
          yLabel="Cohort fertility"
          yMin={0}
          yMax={7.5}
        />

        <p className="finding-prose">
          Three observations:
        </p>
        <ol className="finding-numbered-list">
          <li><strong>The Haredi line is flat.</strong> 6.4 → 6.6 → 6.6 → 6.5 (projected) across four cohorts spanning 35 years of secular modernization. No other line in the chart is flat.</li>
          <li><strong>Israel-Arab Muslim has fallen sharply.</strong> 6.5 → 3.0 over the same window. Same country, same subsidy regime, same general modernization pressure. The structural conditions weakened (urbanization, female education, declining kinship-network density); the fertility followed the conditions, not the subsidies.</li>
          <li><strong>Mormon Utah held above 2.5 but below 3.0.</strong> Consistent with 5-6/7 conditions present (per §5). The cross-case pattern: TFR scales with the structural-condition count, not with policy spending.</li>
        </ol>
      </FindingSection>

      {/* ── §3 The seven structural conditions ── */}
      <FindingSection
        eyebrow="§3 The seven structural conditions"
        title="The mechanism. Per-condition load-bearing weight."
      >
        <p className="finding-prose">
          Seven conditions operate as a stack. Each is independently observable; each can be assessed in any sub-population. The conditions cluster with religious-community density, but the underlying mechanism is structural — the conditions could in principle exist in non-religious form (the kibbutz preview cited in §7 caveats is the historical counter-example).
        </p>

        <FindingSubhead>C1 — Religious-community density</FindingSubhead>
        <p className="finding-prose">
          Peer-parents-visibility approaches 100%. Every adult of childbearing age sees neighbors, siblings, cousins, friends visibly raising large families. There is no "do other people my age really do this?" cognitive friction. The visibility variable is itself a generator of fertility intent — independent of any cognitive valuation of family. In secular Korea-Seoul, peer-parents-visibility for a 30-year-old college-educated woman is ~5-15% (per the SC-PD-001 Lab simulation parameterization).
        </p>

        <FindingSubhead>C2 — Multi-generational household economics</FindingSubhead>
        <p className="finding-prose">
          Housing cost is absorbed by family/community structures, not by individual market rent or mortgage. Three generations under one roof is normative; cousins and siblings cluster within walking distance. The 42%-of-income housing burden that crushes Seoul-CA young-adult fertility intent (per the SC-PD-001 simulation reasoning traces) does not exist in this structure.
        </p>

        <FindingSubhead>C3 — Alternative-to-paid-employment status structure</FindingSubhead>
        <p className="finding-prose">
          The yeshiva / kollel system creates an alternative status hierarchy. Status accrues to Torah scholarship and family size, not to income. A father of seven who studies in kollel has higher community status than a high-paid secular professional. The status-competition that suppresses fertility in secular OECD populations (status earned through career, time, achievement) is absent. Status competition exists but operates on different axes where children are status-positive, not status-suppressing.
        </p>

        <FindingSubhead>C4 — Kinship-network childcare</FindingSubhead>
        <p className="finding-prose">
          Childcare is community-distributed across mothers, sisters, cousins, mothers-in-law, fathers-in-law. The marginal cost of a fourth or fifth child is much lower than in the nuclear-family-only structure where each additional child requires individual-market childcare. The "I can't afford daycare for a third child" calculation that drives down secular OECD fertility intent does not apply.
        </p>

        <FindingSubhead>C5 — Identity-narrative coherence around family</FindingSubhead>
        <p className="finding-prose">
          Large family is not a "lifestyle choice" — it is a high-status religious obligation embedded in identity narrative from childhood. Identity-narrative-coherence around family is approximately 95%. In secular Seoul-Korea, the same variable is ~20% (per the SC-PD-001 Arm A parameterization). This is not a small variable; it is a foundational generator of decisional clarity at the partner-discussion stimulus moment.
        </p>

        <FindingSubhead>C6 — Marriage-formation infrastructure</FindingSubhead>
        <p className="finding-prose">
          Matchmaking systems compress search cost. Median marriage age is ~21 for women, ~22 for men. The 25-34 age window — when secular OECD women are typically resolving career, housing, and partner uncertainty before considering family — is post-marriage and post-first-child for the Haredi cohort. The decision sequence (marriage → first child → second child) starts 4-7 years earlier in the biological window.
        </p>

        <FindingSubhead>C7 — Exit cost</FindingSubhead>
        <p className="finding-prose">
          Leaving the community has real cost: social, economic, identity, family-of-origin. The exit cost stabilizes the structure against drift. This is the condition that prevents the equilibrium from gradually decaying as individual cost-benefit calculations accumulate. In secular OECD societies, "exit" from the high-fertility expectation is costless and is the modal choice.
        </p>

        <FindingSubhead>Per-condition load-bearing weight</FindingSubhead>
        <p className="finding-prose">
          <strong>The load-bearing weights below are unmeasured estimates, not regression coefficients.</strong> They represent mechanism-dossier judgments about how much fertility intent collapses when each condition alone is removed from the otherwise-full stack. The first three (C1, C3, C5) are the structural backbone; removing any one collapses the structure. The remaining four (C2, C4, C6, C7) cause degradation but not collapse.
        </p>
        <p className="finding-prose">
          Each weight is testable, not measured: the framework's predictions are validated (or wounded) by P3 (Mormon Utah cohort completes ≥2.5), P5 (Korean rural Christian condition-count test if data becomes available), and the Refugia Template + Substrate-Symmetric Refugia cross-case patterns. If the framework's specific weight assignments are wrong but the directional ranking (C1, C3, C5 backbone vs the rest supporting/stabilizing) is right, cross-case tests will hold; if the ranking itself is wrong, cross-case tests will fail.
        </p>

        <MagnitudeBars
          title="Per-condition load-bearing weight [unmeasured mechanism-dossier estimates] — sage = backbone (C1, C3, C5), brass = supporting (C2, C4), cognac = stabilizing (C6, C7). Removing C1, C3, or C5 alone collapses the structure. Tested by Refugia Template + Substrate-Symmetric Refugia cross-case predictions."
          items={CONDITION_WEIGHTS}
          valueLabel="weight (estimate)"
        />
      </FindingSection>

      {/* ── §4 Counterfactual removal ── */}
      <FindingSection
        eyebrow="§4 Counterfactual removal"
        title="Predicted fertility intent under per-condition removal."
      >
        <p className="finding-prose">
          To stress-test the seven-condition framework: predict what fertility intent the Haredi cohort would produce if each condition were removed individually, all others held constant. The predictions below are mechanism-dossier estimates from the cross-case extrapolation in §5 (each removal corresponds approximately to a specific other case where that condition is missing).
        </p>

        <MagnitudeBars
          title="Counterfactual fertility intent under per-condition removal [framework projections, not direct observations]. Removing C1 (religious-community density) collapses to ~3.0; the analog is the Israel-Arab Muslim trajectory after rural-to-urban migration weakened density. Removing C3 (status structure) collapses to ~3.5; the analog is modernizing religious sub-populations as professional achievement becomes the dominant status currency. No documented case isolates a single condition removal cleanly; these are the framework's predictions for what such isolation would produce."
          items={COUNTERFACTUAL_REMOVAL}
          valueLabel="cohort TFR (projected)"
        />

        <p className="finding-prose">
          The pattern: backbone conditions (C1, C3, C5) cause collapse to 3.0-4.5; supporting conditions cause degradation to 4.0-5.0; stabilizing conditions cause slow drift toward the underlying structural floor. <strong>The structure is robust to single-condition loss only if the three backbone conditions remain. Lose any backbone condition, and the structure cannot stabilize at high fertility, even with all other conditions present.</strong>
        </p>
      </FindingSection>

      {/* ── §5 Cross-case pattern ── */}
      <FindingSection
        eyebrow="§5 Cross-case pattern"
        title="Conditions count vs cohort TFR — the load-bearing relationship."
      >
        <p className="finding-prose">
          Applying the seven-condition lens to other documented sub-populations and country aggregates produces a clear relationship: cohort TFR scales with the count of structural conditions present, not with policy spending. Below is the cross-case mapping.
        </p>

        <MagnitudeBars
          title="Cross-case: condition count × cohort TFR. The 5+ band sustains TFR ≥ 2.5; the 3-4 band is unstable transitional; the 0-2 band cannot reach replacement. Policy spending is uncorrelated with the count and uncorrelated with the outcome."
          items={CASE_CONDITION_COUNTS}
          valueLabel="cohort TFR"
        />

        <p className="finding-prose">
          Three patterns emerge:
        </p>
        <ol className="finding-numbered-list">
          <li><strong>The 5+ condition band sustains TFR ≥ 2.5.</strong> Israel-Haredi (7/7), Hutterite (7/7), Mormon Utah (5-6/7) all sustain. No exceptions in the documented record.</li>
          <li><strong>The 0-2 condition band cannot reach replacement.</strong> France (1/7), Sweden (0/7), Hungary (0-1/7), Korea (0/7) — all sub-replacement, regardless of policy spending. The Hungarian peak of 1.59 in 2021 was tempo, per the Korea-Hungary finding; cohort fertility is projected at 1.50.</li>
          <li><strong>The 3-4 band is unstable transitional.</strong> Israel-Arab Muslim has fallen 4.5 → 3.0 as conditions weakened (declining from 4-5/7 to 3/7). Roma in Hungary sustains ~3+ but as a small share of population.</li>
        </ol>

        <FindingSubhead>Observed cases vs framework projections — be explicit</FindingSubhead>
        <p className="finding-prose">
          The case matrix above mixes two epistemic registers. Most entries are observed cohort fertility (Israel-Haredi, Hutterite, Mormon Utah, Israel-Arab Muslim, Israel-secular, France, Sweden, Hungary urban, Korea Seoul) — drawn from CBS Israel, Wittgenstein Centre, HFD, or national statistics agencies. <strong>One entry (Korean rural Christian) is a framework projection, not an observation.</strong> Korean fertility statistics are not stratified by religion; no public sub-stratum cohort fertility for Korean rural Christians exists. The 1.4 value is what the seven-condition framework predicts <em>if</em> a Korean rural Christian sub-population exists with documented condition count ≥3/7. P5 in §7 is the explicit pre-registered prediction that tests this; the case is included in the matrix to show what the framework expects, but a critic could legitimately push it out of the chart entirely. The distinction matters: a chart cell that looks like data but is a projection should be tagged as such.
        </p>

        <p className="finding-prose">
          Setting Korean rural Christian aside as a projection, the observed pattern alone establishes the count→TFR relationship: 9 observed cases, monotonic relationship between condition count and cohort fertility, no exceptions. The <em>Positive-Case Search</em> finding's "necessary but insufficient" framing now has quantitative structure: <strong>the lower bound for sustained sub-replacement-region recovery is ~5/7 conditions</strong>; for sustained ≥4.0, the bound is 6-7/7. No OECD policy-driven recovery attempt has produced more than 1/7 conditions, which explains why none has reached cohort fertility ≥ 1.70.
        </p>
      </FindingSection>

      {/* ── §6 Why it's not just subsidies ── */}
      <FindingSection
        eyebrow="§6 Why it's not just subsidies"
        title="The most common counter-argument, addressed directly."
      >
        <p className="finding-prose">
          Haredim receive significant state subsidies — yeshiva stipends, child allowances, housing support. These are real and substantial. They are also not the cause of high fertility. Four direct lines of evidence:
        </p>
        <ol className="finding-numbered-list">
          <li><strong>Subsidy intensity has varied; cohort TFR has not.</strong> Israeli subsidies rose 1990s, fell 2000s under Netanyahu reforms, partially restored 2015s. Haredi cohort fertility tracked at 6.4-6.6 across the entire window with no measurable response to the subsidy variation.</li>
          <li><strong>Israel-Arab Muslim TFR has fallen 4.5 → 3.0 over the same period despite the same general subsidy regime.</strong> Subsidies were equally available; the structural-cultural conditions weakened (urbanization, female education, declining kinship-network density). Fertility followed the conditions.</li>
          <li><strong>Israel-secular Jewish TFR ~2.0-2.2 with the same subsidy access.</strong> Different sub-population, same subsidies, dramatically different fertility. The wedge is the structural-cultural stack, not the spending.</li>
          <li><strong>Cross-comparison: Hungary's per-child subsidies in % GDP exceed Israel's.</strong> Hungary cohort fertility is projected at ~1.50; Haredi cohort fertility ~6.5. If subsidies were causal, the relationship would invert.</li>
        </ol>
        <p className="finding-prose">
          <strong>Subsidy is permissive, not causal.</strong> It removes one specific friction (the "we can't afford another child" calculation at the margin) but does not generate the seven structural conditions. A society can spend Hungary-level GDP on family policy and produce Hungary-level cohort fertility precisely because the structural conditions are absent.
        </p>
      </FindingSection>

      {/* ── §7 Pre-registered predictions ── */}
      <FindingSection
        eyebrow="§7 Pre-registered predictions"
        title="Five predictions. Five resolution dates. One falsifier per prediction."
      >
        <p className="finding-prose">
          The seven-condition framework makes five testable predictions, resolving 2030-2040. Each is registered with central estimate, prediction band, and a falsifier band that — if breached — wounds the framework specifically and the finding generally.
        </p>

        <PredictionsBlock
          title="Pre-registered predictions — Israel-Haredi cohort floor + cross-case extrapolations"
          predictions={HAREDI_PREDICTIONS}
        />

        <p className="finding-prose">
          P1 and P2 test the within-Haredi structural-conditions claim directly. P3 tests cross-case extrapolation to Mormon Utah. P4 tests anti-policy-replication: the prediction is zero state-secular OECD recovery cases, single-falsifier-suffices. P5 tests within-Korea variance: if Korean rural Christian sub-population produces ~0.7 like Korean Seoul, the conditions framework fails to explain within-country variance.
        </p>
      </FindingSection>

      {/* ── §8 Caveats ── */}
      <FindingSection
        eyebrow="§8 Honest accounting"
        title="First-class caveats. Read before citing."
      >
        <FindingSubhead>Specificity vs generalization</FindingSubhead>
        <p className="finding-prose">
          The mechanism is described in Haredi-specific terms. How much abstracts to non-religious contexts is testable but not tested in this finding. The kibbutz movement of 1950s-70s Israel briefly had ~4/7 conditions in secular form; it produced fertility lift but did not sustain (kibbutz cohort fertility collapsed in the 1970s-80s as the structural conditions decayed). The cross-case Form 4 follow-up will operationalize the secular-replication question.
        </p>

        <FindingSubhead>Selection effects</FindingSubhead>
        <p className="finding-prose">
          The sub-population that <em>stays</em> in Haredi life is selected from those who would naturally have higher fertility intent. Magnitude of this selection is unknown and probably non-trivial. However: exit rates from Haredi life have been documented at ~10-15% per generation, which means the high-fertility cohort is not a vanishingly-small selected core; it is the structural majority of the sub-population. P2 (aggregate Haredi TFR ≥5.5 through 2035 despite share rising 13%→22%) is the direct test of whether structural conditions or sample-selection dominate.
        </p>

        <FindingSubhead>Subsidy confound</FindingSubhead>
        <p className="finding-prose">
          Direct counter-argument addressed in §6 but cannot be fully eliminated. A definitive test would require a cohort observation under zero subsidies; this is not feasible. Cross-case evidence (Israel-Arab Muslim TFR falling under the same subsidy regime; Hungary failing to match TFR despite higher subsidies) is the best available.
        </p>

        <FindingSubhead>Social costs</FindingSubhead>
        <p className="finding-prose">
          High TFR is not free. Haredi labor-force participation, especially among men (yeshiva-system), is much lower than Israeli norm. Poverty rates within the community are higher. Educational deficits in secular subjects are documented. <strong>This finding is a mechanism dossier, not normative endorsement.</strong> The question of whether the trade-off is worth it is value-laden and not adjudicated here. The claim is descriptive: this is what sustained sub-replacement-region recovery actually looks like, structurally, in the only OECD case that has produced it.
        </p>

        <FindingSubhead>Israeli-specific factors</FindingSubhead>
        <p className="finding-prose">
          Israel is a high-mobilization society generally — recent wars, existential national framing, military-service-as-shared-experience all may contribute to higher-than-OECD fertility across all Israeli sub-populations (Israel-secular at 2.0-2.2 is itself unusual for OECD secular). The Haredi case is the extreme end of an Israel-wide elevation, not entirely independent of national context. The seven conditions are Haredi-specific operationalizations; the underlying mechanism may have national-context substrate that this finding does not isolate.
        </p>

        <FindingSubhead>Sample of one</FindingSubhead>
        <p className="finding-prose">
          This is a single-case mechanism dossier. Cross-case validation is the Form 4 follow-up (Mormon Utah + Hutterite + Korean rural Christian + Roma in Hungary), not this finding. The seven-condition framework is offered as a hypothesis for that follow-up to test. P3 and P5 are the cross-case validators that resolve before the Form 4 paper would.
        </p>

        <FindingSubhead>Ethnic / religious essentialism risk</FindingSubhead>
        <p className="finding-prose">
          Explicitly framed against. The mechanism is structural and the conditions could in principle exist in non-religious form (kibbutz preview cited above). No evidence yet that secular structural reproduction works at scale. The conditions are not "things only religious people can do" — they are structural arrangements that empirically cluster with religious-community density. Whether the cluster is necessary or contingent is the question P4 (anti-policy-replication) tests over a 14-year window.
        </p>

        <FindingSubhead>Reflexivity rating: HIGH</FindingSubhead>
        <p className="finding-prose">
          Demographic predictions can influence the systems being predicted. A widely-cited finding that "religious-community structure is the only OECD case of sustained fertility recovery" can produce panic, sectarian backlash, or mistaken policy mimicry that copies the religion without the structure. <strong>Framed as diagnostic + structural-condition map, not policy advice or normative endorsement.</strong>
        </p>
      </FindingSection>

      {/* ── §9 Cross-references ── */}
      <FindingSection
        eyebrow="§9 What this means for the next question"
        title="The corpus arc, completed."
      >
        <p className="finding-prose">
          This finding closes the four-finding arc that the Continuum's popdec project committed to:
        </p>
        <ul className="finding-bullets">
          <li><strong>Korea-Hungary Divergence (Form 2 — Pair).</strong> The static negative thesis: spending alone does not reproduce sustained recovery. Israel-Haredi documents what the structural-cultural alternative actually is.</li>
          <li><strong>Korea's Lock-In (Form 5 — Counterfactual walk).</strong> The dynamic negative thesis: Korea's six branch points 1997-2024 closed early. Israel-Haredi's seven structural conditions operationalize what those branches would have had to construct.</li>
          <li><strong>Positive-Case Search (Form 3 — Falsification test).</strong> The forward negative thesis: zero OECD policy-driven recovery to 1.70 cohort fertility by 2035. This finding operationalizes the load-bearing contrast (Israel) named there.</li>
          <li><strong>Israel-Haredi Floor (Form 1 — Single-case mechanism dossier).</strong> The structural-positive thesis: here is what sustained recovery actually looks like, in the only OECD case that has produced it.</li>
        </ul>

        <p className="finding-prose">
          Reading any single finding weakens the structural argument. Reading all four together makes it bulletproof.
        </p>

        <FindingSubhead>Open questions for the next finding</FindingSubhead>
        <ul className="finding-bullets">
          <li><strong>Cross-case Form 4 paper.</strong> Does the seven-condition framework survive cross-case validation across Mormon Utah, Hutterite, Roma in Hungary, Korean rural Christian, and Israel-secular? P3 and P5 are the within-corpus validators; the Form 4 paper is the formal test.</li>
          <li><strong>Secular-reproduction question.</strong> Can the seven conditions exist in non-religious form? The kibbutz preview is the historical near-counter-example. P4 (anti-policy-replication) tests this over the 2025-2040 window.</li>
          <li><strong>Backbone-vs-supporting test.</strong> Are C1, C3, C5 actually the backbone? A targeted Lab simulation could vary which conditions are present in agent traits and observe whether fertility intent collapses faster when backbone vs supporting conditions are removed. SC-PD-006 candidate card.</li>
          <li><strong>Resolution monitoring.</strong> P1 (2030), P3 (2030+), P5 (2030+), P2 (2035), P4 (2040) come due over the next 14 years. The Track Record updates as each resolves.</li>
        </ul>

        <FindingSubhead>Cross-references</FindingSubhead>
        <p className="finding-prose">
          This finding is the structural-positive completion of <code>lab:finding/popdec/2026/korea-hungary-divergence/v1</code> (static negative), <code>lab:finding/popdec/2026/korea-lockin/v1</code> (dynamic negative), and <code>lab:finding/popdec/2026/positive-case-search/v1</code> (forward negative). All four should be read together.
        </p>
      </FindingSection>

      <footer className="finding-footer">
        <div className="finding-footer-eyebrow">Footer ledger</div>
        <dl className="finding-meta">
          <dt>Methodology Version</dt><dd>Lab V2.0</dd>
          <dt>Form</dt><dd>Form 1 — Single-case mechanism dossier (first Form 1 in the corpus)</dd>
          <dt>Reflexivity</dt><dd>HIGH — widely-cited findings about fertility mechanism can produce policy mimicry, sectarian backlash, or panic</dd>
          <dt>Epistemic status</dt><dd>Mechanism documented · cross-case validation pending Form 4 follow-up</dd>
          <dt>Sample size</dt><dd>N=1 primary case (Israel-Haredi); N=4 partial cross-case anchors (Hutterite, Mormon Utah, Roma, Korean rural Christian, Israel-secular, Israel-Arab)</dd>
          <dt>Pre-registration date</dt><dd>2026-05-08. Resolution: 2030 (P1, P3, P5), 2035 (P2), 2040 (P4)</dd>
          <dt>Visibility</dt><dd>Public · read-only</dd>
          <dt>Citation handle</dt><dd><code>{FINDING_META.citationHandle}</code></dd>
          <dt>Falsifier</dt><dd>Any one of: P1 cohort &lt;5.5; P2 aggregate &lt;5.0 without exogenous shock; OECD case with &lt;3/7 conditions sustains TFR ≥4.5; Mormon Utah cohort &lt;2.2.</dd>
        </dl>
        <div className="finding-citation">
          <strong>Cite as:</strong> Mai, T. (2026). The Six-Plus Floor: How Israel-Haredi Sustains Cohort Fertility ≥ 6.0 in an OECD Context, and What It Reveals About the Structural Alternatives to Policy. Lab Finding {FINDING_META.citationHandle}.
        </div>
        <div className="finding-version">
          <div className="finding-footer-eyebrow">Update history</div>
          <p>v1 — 2026-05-08 — initial pre-registration. Resolution windows: 2030, 2035, 2040.</p>
        </div>
      </footer>
    </article>
  );
}
